import { useRef, type KeyboardEvent } from 'react';
import { Box, ButtonBase, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ShareRole } from '@/api/shares';
import { resolveTokens } from '@/theme/theme';

const ROLES: readonly ShareRole[] = ['VIEWER', 'MANAGER'];

/**
 * The Read only / Manager segmented control (TMMAPP-0110; audit 06 § C4, `reference/v2/13-*`): a radio group
 * named by `label` ("{person} access", §34 item 11), segments 5 10, 12/500. The selected segment is
 * the `selected` layer with `link` text on the opaque `surface` track (the SegmentedControl pair).
 * Arrow keys move and choose, as a radio group does; each choice is the caller's save.
 */
export function RoleToggle({
  value,
  label,
  onChange,
  disabled = false,
}: {
  value: ShareRole;
  label: string;
  onChange: (role: ShareRole) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function choose(role: ShareRole) {
    if (disabled || role === value) return;
    onChange(role);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + ROLES.length) % ROLES.length;
    refs.current[next]?.focus();
    choose(ROLES[next]!);
  }

  return (
    <Box
      role="radiogroup"
      aria-label={label}
      aria-disabled={disabled || undefined}
      data-role-toggle=""
      sx={{
        display: 'inline-flex',
        flexShrink: 0,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: '4px',
        overflow: 'hidden',
      }}
    >
      {ROLES.map((role, index) => {
        const checked = role === value;
        return (
          <ButtonBase
            key={role}
            ref={(node: HTMLButtonElement | null) => {
              refs.current[index] = node;
            }}
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            disabled={disabled}
            onClick={() => choose(role)}
            onKeyDown={(event) => onKeyDown(event, index)}
            sx={{
              p: '5px 10px',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '18px',
              color: checked ? tokens.link : tokens.text,
              bgcolor: checked ? tokens.selected : 'transparent',
              borderInlineStart: index > 0 ? `1px solid ${tokens.line}` : 'none',
              '&:hover': { bgcolor: checked ? tokens.selected : tokens.hover },
              '&.Mui-focusVisible': { outline: `2px solid ${tokens.link}`, outlineOffset: '-2px' },
              '&.Mui-disabled': { color: checked ? tokens.link : tokens.text2 },
            }}
          >
            {t(role === 'MANAGER' ? 'roles.manager' : 'roles.viewer')}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
